import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { SERVICES } from "../../data/services";
import { SITE_CONFIG } from "../../config/site";
import { ThemeSwitcher } from "./ThemeSwitcher";
import { Headset } from "lucide-react";

const LINKS = [
  { to: "/", label: "Inicio" },
  { to: "/inteligencia-artificial", label: "IA" },
  { to: "/automatizacion", label: "Automatización" },
  { to: "/proyectos", label: "Proyectos" },
  { to: "/nosotros", label: "Nosotros" },
  { to: "/contacto", label: "Contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location.pathname]);

  const linkCls = ({ isActive }) =>
    `relative text-sm font-medium transition-colors hover:text-[var(--ink)] ${isActive ? "text-[var(--ink)]" : "text-[var(--muted)]"}`;

  return (
    <header className="fixed top-0 inset-x-0 z-[80]" data-testid="navbar">
      <div
        className={`mx-auto max-w-7xl px-4 sm:px-6 transition-all duration-500 ${scrolled ? "pt-3" : "pt-5"}`}
      >
        <div
          className={`flex items-center justify-between rounded-2xl px-5 py-3 transition-all duration-500 ${
            scrolled ? "glass border border-[var(--line)] shadow-[0_8px_40px_rgba(0,0,0,0.25)]" : "border border-transparent"
          }`}
        >
          <Link to="/" className="flex items-center gap-2.5" data-testid="navbar-logo" aria-label="Netsys Panamá — Inicio">
            <span className="w-2.5 h-2.5 rounded-[3px] bg-gradient-to-br from-[var(--green)] to-[var(--blue)] animate-pulse-dot" />
            <span className="text-lg font-extrabold tracking-tight">
              Netsys<span className="text-gradient"> Panamá</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-7" aria-label="Navegación principal">
            <NavLink to="/" className={linkCls} data-testid="nav-inicio">Inicio</NavLink>
            <div className="relative" onMouseEnter={() => setDropOpen(true)} onMouseLeave={() => setDropOpen(false)}>
              <button className="flex items-center gap-1 text-sm font-medium text-[var(--muted)] hover:text-[var(--ink)] transition-colors" data-testid="nav-soluciones" aria-expanded={dropOpen}>
                Soluciones <ChevronDown size={14} className={`transition-transform ${dropOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {dropOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25 }}
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-4 w-[520px]"
                  >
                    <div className="glass rounded-2xl border border-[var(--line)] p-4 grid grid-cols-2 gap-1 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/${s.slug}`}
                          onClick={() => setDropOpen(false)}
                          className="group flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-[var(--surface2)] transition-colors"
                          data-testid={`nav-service-${s.slug}`}
                        >
                          <span className="mono text-[10px] text-[var(--green)] mt-1">{s.num}</span>
                          <span className="text-sm font-medium text-[var(--ink)]/90 group-hover:text-[var(--ink)]">{s.title}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {LINKS.slice(1).map((l) => (
              <NavLink key={l.to} to={l.to} className={linkCls} data-testid={`nav-${l.label.toLowerCase().replace("ó", "o")}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <ThemeSwitcher />
            <a
              href={SITE_CONFIG.supportUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-glow hidden sm:inline-flex items-center gap-1.5 rounded-full bg-[var(--surface2)] px-5 py-2.5 text-sm font-semibold hover:bg-[var(--surface)] transition-colors"
              data-testid="nav-cta-soporte"
              data-cursor="cta"
            >
              <Headset size={15} className="text-[var(--green)]" /> Soporte remoto
            </a>
            <button
              className="lg:hidden p-2 text-[var(--ink)]"
              onClick={() => setMobileOpen(true)}
              aria-label="Abrir menú"
              data-testid="nav-mobile-open"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[85] bg-[var(--bg)]/95 backdrop-blur-xl flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 pt-7">
              <span className="text-lg font-extrabold">Netsys<span className="text-gradient"> Panamá</span></span>
              <button onClick={() => setMobileOpen(false)} aria-label="Cerrar menú" className="p-2" data-testid="nav-mobile-close">
                <X size={24} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Navegación móvil">
              {[{ to: "/", label: "Inicio" }, ...SERVICES.slice(0, 5).map((s) => ({ to: `/${s.slug}`, label: s.title })), ...LINKS.slice(3)].map((l, i) => (
                <motion.div key={l.to + l.label} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <Link to={l.to} onClick={() => setMobileOpen(false)} className="block py-2.5 text-3xl font-bold tracking-tight text-[var(--ink)]/90 hover:text-[var(--green)] transition-colors">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.a
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                href={SITE_CONFIG.supportUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-glow mt-6 w-max rounded-full bg-[var(--surface2)] px-7 py-3.5 text-sm font-semibold inline-flex items-center gap-2"
                data-testid="mobile-cta-soporte"
              >
                <Headset size={16} className="text-[var(--green)]" /> Soporte remoto
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
